import { reinitOnSwap } from '../Utility/SwapReinit'

// VHWWEB-107 — "Letzte Suche" der Fortbildungssuche als Flyout-Block (Figma
// 2801-30051): öffnet beim Fokus auf das leere Suchfeld, wird beim Tippen vom
// Autocomplete abgelöst. Eigener localStorage-Key, getrennt vom Header.
const STORAGE_KEY = 'vhw.courseSearch.lastTerms'
const QUERY_PARAM = 'tx_sitepackage_coursesearch[q]'
const MAX_ENTRIES = 8
const MAX_AGE_DAYS = 30

interface StoredTerm {
  query: string
  timestamp: number
}

class CourseLastSearchedTerms {
  private readonly wraps: NodeListOf<HTMLElement>

  constructor() {
    this.wraps = document.querySelectorAll<HTMLElement>(
      '[data-js="search-form"][data-last-terms="course"]'
    )
  }

  public init(): void {
    this.wraps.forEach((wrap: HTMLElement): void => {
      this.initWrap(wrap)
    })
  }

  private addTerm(query: string): void {
    const trimmed = query.trim()
    if (trimmed.length === 0) return

    const terms = this.readTerms().filter((t) => t.query.toLowerCase() !== trimmed.toLowerCase())
    terms.unshift({ query: trimmed, timestamp: Date.now() })

    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(terms.slice(0, MAX_ENTRIES)))
    } catch {
      // Storage quota / disabled — silently ignore.
    }
  }

  private clearAll(container: HTMLElement): void {
    const hadFocusWithin = container.contains(document.activeElement)
    localStorage.removeItem(STORAGE_KEY)
    this.renderTerms(container)
    if (hadFocusWithin) this.focusInput(container)
  }

  private close(panel: HTMLElement): void {
    panel.setAttribute('hidden', '')
  }

  private escape(text: string): string {
    return text
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
  }

  private focusAfterRemoval(container: HTMLElement, removedIndex: number): void {
    const remaining = container.querySelectorAll<HTMLElement>('[data-js="course-last-searched-remove"]')

    if (remaining.length > 0) {
      remaining[Math.min(removedIndex, remaining.length - 1)]?.focus()
      return
    }

    this.focusInput(container)
  }

  private focusInput(container: HTMLElement): void {
    container.closest<HTMLElement>('[data-js="search-form"][data-last-terms="course"]')
      ?.querySelector<HTMLElement>('[data-js="search-form-input"]')
      ?.focus()
  }

  private initWrap(wrap: HTMLElement): void {
    const input = wrap.querySelector<HTMLInputElement>('[data-js="search-form-input"]')
    const panel = wrap.querySelector<HTMLElement>('[data-js="course-last-searched-panel"]')
    const container = wrap.querySelector<HTMLElement>('[data-js="course-last-searched"]')
    const form = wrap.closest<HTMLFormElement>('form')

    if (!input || !panel || !container) return

    this.renderTerms(container)

    input.addEventListener('focus', (): void => {
      if (input.value.trim().length === 0) {
        this.open(panel)
      }
    })

    // Autocomplete takes over as soon as the user types.
    input.addEventListener('input', (): void => {
      this.close(panel)
    })

    input.addEventListener('keydown', (event: KeyboardEvent): void => {
      if (event.key === 'Escape') {
        this.close(panel)
      }
    })

    const outsideClick = (event: MouseEvent): void => {
      if (!document.contains(wrap)) {
        document.removeEventListener('click', outsideClick)
        return
      }
      if (!wrap.contains(event.target as Node)) {
        this.close(panel)
      }
    }
    document.addEventListener('click', outsideClick)

    form?.addEventListener('submit', (): void => {
      const query = input.value.trim()
      if (query.length > 0) {
        this.addTerm(query)
        this.renderTerms(container)
      }
    })
  }

  private open(panel: HTMLElement): void {
    const lastSearched = panel.querySelector('.search-last-searched')
    const hasTerms = lastSearched !== null && !lastSearched.hasAttribute('hidden')
    const hasFrequent = panel.querySelector('.search-frequent') !== null

    if (!hasTerms && !hasFrequent) return

    panel.removeAttribute('hidden')
  }

  private readTerms(): StoredTerm[] {
    try {
      const raw = localStorage.getItem(STORAGE_KEY)
      if (!raw) return []

      const parsed = JSON.parse(raw) as unknown
      if (!Array.isArray(parsed)) return []

      const cutoff = Date.now() - MAX_AGE_DAYS * 24 * 60 * 60 * 1000
      return parsed
        .filter((entry): entry is StoredTerm =>
          typeof entry === 'object'
          && entry !== null
          && typeof (entry as StoredTerm).query === 'string'
          && typeof (entry as StoredTerm).timestamp === 'number'
        )
        .filter((t) => t.timestamp > cutoff)
    } catch {
      return []
    }
  }

  private removeTerm(query: string, container: HTMLElement, sourceBtn: HTMLElement): void {
    const buttons = Array.from(container.querySelectorAll<HTMLElement>('[data-js="course-last-searched-remove"]'))
    const removedIndex = buttons.indexOf(sourceBtn)

    const terms = this.readTerms().filter((t) => t.query !== query)
    localStorage.setItem(STORAGE_KEY, JSON.stringify(terms))
    this.renderTerms(container)

    this.focusAfterRemoval(container, removedIndex)
  }

  private renderTerms(container: HTMLElement): void {
    const terms = this.readTerms()

    if (terms.length === 0) {
      container.innerHTML = ''
      container.setAttribute('hidden', '')
      return
    }

    const searchUrl = (container.dataset.searchUrl ?? '') || window.location.pathname
    const labelTitle = container.dataset.labelTitle ?? ''
    const labelClear = container.dataset.labelClear ?? ''
    const labelRemove = container.dataset.labelRemove ?? ''
    const param = encodeURIComponent(QUERY_PARAM)

    container.innerHTML = `
      <div class="search-last-searched__header">
        <span class="search-last-searched__icon" aria-hidden="true"></span>
        <p class="search-last-searched__title">${this.escape(labelTitle)}</p>
        <button class="search-last-searched__clear" data-js="course-last-searched-clear" type="button">
          ${this.escape(labelClear)}
        </button>
      </div>
      <ul class="search-last-searched__list">
        ${terms.map((t) => `
          <li class="search-last-searched__item">
            <a class="search-last-searched__chip" data-js="last-searched-chip" href="${this.escape(searchUrl)}?${param}=${encodeURIComponent(t.query)}">
              ${this.escape(t.query)}
            </a>
            <button class="search-last-searched__remove" data-js="course-last-searched-remove" data-query="${this.escape(t.query)}" type="button"
              aria-label="${this.escape(labelRemove)}: ${this.escape(t.query)}"></button>
          </li>
        `).join('')}
      </ul>
    `

    container.removeAttribute('hidden')

    container.querySelector('[data-js="course-last-searched-clear"]')
      ?.addEventListener('click', (): void => this.clearAll(container))

    container.querySelectorAll('[data-js="course-last-searched-remove"]').forEach((btn) => {
      btn.addEventListener('click', (event: Event): void => {
        // `stopPropagation` is load-bearing: removeTerm re-renders the list, so by
        // the time the click reaches the document-level outside-click handler the
        // target is detached, `wrap.contains(target)` is false, and the panel would
        // close — preventing the user from deleting several terms in a row.
        event.stopPropagation()
        const query = (btn as HTMLElement).dataset.query ?? ''
        this.removeTerm(query, container, btn as HTMLElement)
      })
    })
  }
}

const courseLastSearchedTerms = new CourseLastSearchedTerms()
courseLastSearchedTerms.init()
reinitOnSwap(() => new CourseLastSearchedTerms())
